// qs-useAppInitialization.ts
import { useDrawingManagement } from '@/composables/qs-useDrawingManagement'
import { useHistory } from '@/composables/qs-useHistory'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useGalleryStore, type Drawing } from '@/stores/qs-store-gallery'
import { useLayerStore } from '@/stores/qs-store-layers'
import { usePanelStore } from '@/stores/qs-store-panels'
import { useSilhouetteStore } from '@/stores/qs-store-silhouette'
import { useToolStore } from '@/stores/qs-store-tools'
import { computed, nextTick, ref } from 'vue'

export function useAppInitialization() {
  const isInitializing = ref(true)
  const initializationStep = ref('')
  const initializationError = ref<string | null>(null)

  const storesReady = ref({
    canvas: false,
    layers: false,
    tools: false,
    gallery: false,
    panels: false,
    silhouette: false,
    history: false
  })

  const isAppReady = computed(() => {
    return !isInitializing.value &&
           Object.values(storesReady.value).every(ready => ready) &&
           !initializationError.value
  })

  const initializationProgress = computed(() => {
    const totalSteps = Object.keys(storesReady.value).length
    const completedSteps = Object.values(storesReady.value).filter(ready => ready).length
    return Math.round((completedSteps / totalSteps) * 100)
  })

  const initializeApp = async (): Promise<void> => {
    try {
      isInitializing.value = true
      initializationError.value = null

      await initializeCoreStores()
      await initializeCanvasAndLayers()
      await initializeUIStores()
      await initializeDrawingEnvironment()
      await validateInitialization()

      isInitializing.value = false
    } catch (error) {
      console.error('❌ App initialization failed:', error)
      initializationError.value = error instanceof Error ? error.message : 'Unknown initialization error'
      isInitializing.value = false
    }
  }

  const initializeCoreStores = async (): Promise<void> => {
    initializationStep.value = 'Initializing core systems...'

    useToolStore()
    storesReady.value.tools = true

    useLayerStore()
    storesReady.value.layers = true

    useCanvasStore()
    storesReady.value.canvas = true

    useHistory()
    storesReady.value.history = true
  }

  const initializeCanvasAndLayers = async (): Promise<void> => {
    initializationStep.value = 'Setting up canvas and layers...'
    await nextTick()

    useCanvasStore()
  }

  const initializeUIStores = async (): Promise<void> => {
    initializationStep.value = 'Loading UI components...'

    useGalleryStore()
    storesReady.value.gallery = true

    usePanelStore()
    storesReady.value.panels = true

    useSilhouetteStore()
    storesReady.value.silhouette = true
  }

  const initializeDrawingEnvironment = async (): Promise<void> => {
    initializationStep.value = 'Setting up drawing environment...'

    const canvasStore = useCanvasStore()
    const toolStore = useToolStore()
    const galleryStore = useGalleryStore()

    const currentDrawing = galleryStore.currentDrawing
    if (currentDrawing) {
      await loadDrawing(currentDrawing)
    } else {
      const defaultDrawing = galleryStore.createNewDrawing()
      await loadDrawing(defaultDrawing)

      const { saveCurrentDrawing } = useDrawingManagement()
      saveCurrentDrawing()
    }

    const history = useHistory()
    await history.initializeHistory()

    canvasStore.setBackgroundColor(toolStore.backgroundColor)

    await nextTick()
    const { forceSetDefaultBrushSize } = await import('@/config/qs-brushDefaults')
    forceSetDefaultBrushSize()
  }

  const loadDrawing = async (drawing: Drawing): Promise<void> => {
    const { loadDrawing: loadDrawingFromManagement } = useDrawingManagement()
    loadDrawingFromManagement(drawing)
    await nextTick()
  }

  const validateInitialization = async (): Promise<void> => {
    initializationStep.value = 'Validating initialization...'

    const stores = [
      useCanvasStore(),
      useLayerStore(),
      useToolStore(),
      useGalleryStore(),
      usePanelStore(),
      useSilhouetteStore()
    ]

    for (const store of stores) {
      if (!store) {
        throw new Error('Store not properly initialized')
      }
    }

    const canvasStore = useCanvasStore()
    if (canvasStore.width <= 0 || canvasStore.height <= 0) {
      throw new Error('Canvas dimensions not properly set')
    }

    const layerStore = useLayerStore()
    if (layerStore.layers.length === 0) {
      throw new Error('No layers available')
    }
  }

  const resetInitialization = (): void => {
    isInitializing.value = true
    initializationError.value = null
    initializationStep.value = ''

    Object.keys(storesReady.value).forEach(key => {
      storesReady.value[key as keyof typeof storesReady.value] = false
    })
  }

  const getInitializationStatus = () => {
    return {
      isInitializing: isInitializing.value,
      isAppReady: isAppReady.value,
      currentStep: initializationStep.value,
      error: initializationError.value,
      progress: initializationProgress.value,
      storesReady: { ...storesReady.value }
    }
  }

  return {
    isInitializing,
    isAppReady,
    initializationStep,
    initializationError,
    initializationProgress,
    storesReady,
    initializeApp,
    resetInitialization,
    getInitializationStatus
  }
}